/**
 * Admin Login Page
 * Renders Cloudflare Turnstile widget if enabled in settings.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { getPublicPanelInfo } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tv, Lock, User as UserIcon, AlertCircle } from "lucide-react";

const LOGIN_TIDS = {
  username: "login-username",
  password: "login-password",
  submit: "login-submit",
  error: "login-error",
  turnstileWidget: "login-turnstile-widget",
};

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

function formatErrorDetail(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

function formatLoginError(err) {
  // 1. Network error (no response) — browser blocked, offline, or CORS
  if (!err.response) {
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server. Check your internet connection.";
    }
    if (err.code === "ECONNABORTED") {
      return "Server took too long to respond. Try again in 30 seconds.";
    }
    return err.message || "Network error. Try again in 30 seconds.";
  }
  // 2. Backend responded but not with JSON (Cloudflare challenge / 502 / 504)
  const status = err.response.status;
  const data = err.response.data;
  const isJsonObject = data && typeof data === "object" && !Array.isArray(data);
  if (!isJsonObject) {
    if (status === 503 || status === 504)
      return "Server is temporarily overloaded. Try again in 30 seconds.";
    if (status === 502)
      return "Cannot reach the backend right now. Try again in 30 seconds.";
    return `Unexpected response from server (HTTP ${status}). Try again in 30 seconds.`;
  }
  // 3. JSON response — read the detail, fall back to generic message
  return formatErrorDetail(data.detail);
}

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", resolve);
      return;
    }
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [panelName, setPanelName] = useState("ZA Player Pro Admin");
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  useEffect(() => {
    getPublicPanelInfo()
      .then((d) => {
        setPanelName(d.panel_name);
        if (d.turnstile_enabled && d.turnstile_site_key) {
          setTurnstileEnabled(true);
          setTurnstileSiteKey(d.turnstile_site_key);
        }
      })
      .catch(() => {});
  }, []);

  // Render Turnstile widget once we know the site key
  useEffect(() => {
    if (!turnstileEnabled || !turnstileSiteKey || !turnstileContainerRef.current) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        // Wait a tick for window.turnstile to be defined
        const tryRender = () => {
          if (cancelled) return;
          if (!window.turnstile) {
            setTimeout(tryRender, 100);
            return;
          }
          if (!turnstileContainerRef.current) return;
          // Clear previous widget if any
          if (turnstileWidgetIdRef.current) {
            try {
              window.turnstile.remove(turnstileWidgetIdRef.current);
            } catch {
              /* ignore */
            }
          }
          turnstileWidgetIdRef.current = window.turnstile.render(
            turnstileContainerRef.current,
            {
              sitekey: turnstileSiteKey,
              theme: "dark",
              callback: (token) => setTurnstileToken(token),
              "expired-callback": () => setTurnstileToken(null),
              "error-callback": () => setTurnstileToken(null),
            }
          );
        };
        tryRender();
      })
      .catch(() => setError("Failed to load Cloudflare Turnstile. Try again."));
    return () => {
      cancelled = true;
    };
  }, [turnstileEnabled, turnstileSiteKey]);

  useEffect(() => {
    // If already logged in, redirect
    if (user && user !== false) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (turnstileEnabled && !turnstileToken) {
      setError("Please complete the Cloudflare challenge.");
      return;
    }

    setLoading(true);
    try {
      await login(username, password, turnstileToken);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatLoginError(err));
      // Reset Turnstile after failed attempt
      if (turnstileEnabled && turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(turnstileWidgetIdRef.current);
          setTurnstileToken(null);
        } catch {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background grain effect */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 30%, rgba(249, 115, 22, 0.15) 0%, transparent 50%), radial-gradient(circle at 75% 70%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)",
        }}
      />

      <Card className="relative w-full max-w-md bg-slate-900 border-slate-800 p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-7">
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center mb-3 shadow-lg shadow-orange-500/20">
            <Tv className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">{panelName}</h1>
          <p className="text-sm text-slate-400 mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username" className="text-slate-300 text-sm">
              Username
            </Label>
            <div className="relative mt-1.5">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="username"
                data-testid={LOGIN_TIDS.username}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Admin"
                required
                autoFocus
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-slate-300 text-sm">
              Password
            </Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="password"
                data-testid={LOGIN_TIDS.password}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                required
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
              />
            </div>
          </div>

          {turnstileEnabled && (
            <div
              data-testid={LOGIN_TIDS.turnstileWidget}
              ref={turnstileContainerRef}
              className="flex justify-center"
            />
          )}

          {error && (
            <div
              data-testid={LOGIN_TIDS.error}
              className="flex items-start gap-2 bg-red-950/40 border border-red-900/50 text-red-300 px-3 py-2.5 rounded-md text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            data-testid={LOGIN_TIDS.submit}
            type="submit"
            disabled={loading || (turnstileEnabled && !turnstileToken)}
            className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white font-semibold py-2.5 disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>

          <div className="text-center pt-1">
            <Link
              to="/admin/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-xs text-slate-400 hover:text-orange-400 transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

export { LOGIN_TIDS };
